import { filterClasses } from "@swivl-mono/helpers";
import { Fragment, HTMLAttributes, MutableRefObject, RefObject, forwardRef } from "react";
import { IoIosArrowDown } from "react-icons/io";
import { IoClose } from "react-icons/io5";
import { LuSearch } from "react-icons/lu";

import { Tooltip } from "../Tooltip/Tooltip";
import { SelectOption, SelectSize, SelectVariant } from "./Select";
import { SelectChip } from "./Select.Chip";

interface SelectTriggerProps {
  chipContainerRef: RefObject<HTMLDivElement>;
  disabled: boolean;
  error: string | Error | null;
  hasSearchTrigger: boolean;
  inputRef: RefObject<HTMLInputElement>;
  isChipVariant: boolean;
  isInputFocused: boolean;
  isMultiSelect: boolean;
  isPopoverOpen: boolean;
  name?: string;
  onClearAll: () => void;
  onInputBlur: (e: React.FocusEvent<HTMLInputElement>) => void;
  onInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onInputFocus: () => void;
  placeholder?: string;
  required: boolean;
  searchQuery: string;
  selectContainerRef: RefObject<HTMLDivElement>;
  selectedOptions: SelectOption[];
  /** Append the selected option's `sublabel` to the trigger's text. */
  showSublabelInTrigger?: boolean;
  size: SelectSize;
  variant: SelectVariant;
  visibleChipCount: number | null;
  hideClear?: boolean;
  id?: string;
  onRemoveOption: (value: string) => void;
}

export const SelectTrigger = forwardRef<HTMLDivElement, SelectTriggerProps & HTMLAttributes<HTMLDivElement>>(
  (
    {
      chipContainerRef,
      disabled,
      error,
      hasSearchTrigger,
      inputRef,
      isChipVariant,
      isInputFocused,
      isMultiSelect,
      isPopoverOpen,
      name,
      onClearAll,
      onInputBlur,
      onInputChange,
      onInputFocus,
      placeholder,
      required,
      searchQuery,
      selectContainerRef,
      selectedOptions,
      showSublabelInTrigger = false,
      size,
      variant,
      visibleChipCount,
      hideClear,
      id,
      onRemoveOption,
      ...props
    },
    ref
  ) => {
    // merge the forwarded ref from popover with selectcontainerref
    const mergedRef = (node: HTMLDivElement | null) => {
      if (selectContainerRef) {
        (selectContainerRef as MutableRefObject<HTMLDivElement | null>).current = node;
      }
      // assign to forwarded ref for popover
      if (typeof ref === "function") {
        ref(node);
      } else if (ref) {
        (ref as MutableRefObject<HTMLDivElement | null>).current = node;
      }
    };

    const hiddenChipCount = visibleChipCount !== null ? selectedOptions.length - visibleChipCount : 0;
    const shouldChipsCollapse = isInputFocused && hiddenChipCount > 0;

    const isFilled = !!selectedOptions.length;
    const hasValue = selectedOptions.length > 0;

    const getInputValue = () => {
      // chip variant always shows comma-separated labels (no search in trigger)
      if (isChipVariant && hasValue) {
        return selectedOptions.map((opt) => opt.label).join(", ");
      }
      // when closed with a selected value - for single select
      if (!isMultiSelect && !isPopoverOpen && hasValue) {
        return selectedOptions[0]?.label || "";
      }
      // when open or typing, show the search query
      return searchQuery;
    };

    const getPlaceholderText = () => {
      // don't show placeholder when there's a selected value
      if (hasValue && (!isMultiSelect || isChipVariant)) {
        return "";
      }
      const defaultPlaceholder = hasSearchTrigger ? "Select or Search" : "Select";
      // custom placeholder or default
      return placeholder || defaultPlaceholder;
    };

    const inputValue = getInputValue();
    const placeholderText = getPlaceholderText();

    const selectSizes = {
      [SelectSize.EXTRA_SMALL]: "h-[18px]",
      [SelectSize.SMALL]: "h-[26px]",
      [SelectSize.MEDIUM]: "h-[34px]"
    };

    const variantStylesMap = {
      chip: {
        border: "border-none",
        background: "bg-transparent",
        padding: "px-2 py-0.5",
        text: "text-sm text-black",
        focus: "hover:bg-grey-50 data-[open=true]:bg-grey-100",
        disabledBg: "data-[disabled=true]:hover:bg-transparent"
      },
      search: {
        border: "border border-grey-400",
        background: "bg-gradient-to-b from-grey-50 via-white via-20% to-white",
        padding: "pr-3",
        text: "text-sm data-[filled=false]:text-grey-600 data-[filled=true]:text-black",
        focus: "focus-within:border-orange-500 focus-within:ring-2 focus-within:ring-orange-500/20 hover:border-orange-500",
        disabledBg: "data-[disabled=true]:from-grey-200 data-[disabled=true]:via-grey-200 data-[disabled=true]:to-grey-200"
      },
      default: {
        border: "border border-grey-400",
        background: "bg-gradient-to-b from-white to-grey-50",
        padding: "pr-3",
        text: "text-sm data-[filled=false]:text-grey-600 data-[filled=true]:text-black",
        focus: "focus-within:border-orange-500 focus-within:ring-2 focus-within:ring-orange-500/20 hover:border-orange-500",
        disabledBg: "data-[disabled=true]:from-grey-200 data-[disabled=true]:to-grey-200"
      }
    };

    const getVariantKey = () => {
      if (isChipVariant) return "chip";
      if (hasSearchTrigger) return "search";
      return "default";
    };

    const variantStyles = variantStylesMap[getVariantKey()];

    return (
      <div
        {...props}
        ref={mergedRef}
        data-filled={isFilled}
        data-error={!!error}
        data-disabled={disabled}
        data-open={isPopoverOpen}
        className={filterClasses(
          "group relative flex cursor-pointer items-center gap-1 ring-offset-0 transition-all duration-200 ease-in-out",
          isChipVariant ? "w-fit rounded" : "w-full rounded-sm",
          !isChipVariant && selectSizes[size],
          variantStyles.border,
          variantStyles.background,
          variantStyles.padding,
          variantStyles.text,
          variantStyles.focus,
          variantStyles.disabledBg,
          "data-[error=true]:border-red-500",
          "data-[disabled=true]:cursor-not-allowed data-[disabled=true]:text-grey-500 data-[disabled=true]:hover:border-grey-400"
        )}
        onClick={(e) => {
          props.onClick?.(e);
          if (!disabled && !hasSearchTrigger) {
            onInputFocus();
          }
        }}
      >
        {/* multi-select chips */}
        {isMultiSelect && !isChipVariant && selectedOptions.length > 0 && (
          <>
            <div
              className={filterClasses("ml-1 flex h-full gap-1 py-1", shouldChipsCollapse && "pointer-events-none absolute -translate-x-2 opacity-0")}
              ref={chipContainerRef}
            >
              {selectedOptions.map((option, index) => {
                const isHidden = visibleChipCount !== null && index >= visibleChipCount;
                return (
                  <SelectChip
                    className={isHidden ? "pointer-events-none absolute hidden" : ""}
                    key={option.value}
                    option={option}
                    handleRemoveOption={onRemoveOption}
                  />
                );
              })}
            </div>
            {hiddenChipCount > 0 && (
              <div>
                <Tooltip
                  content={selectedOptions
                    .slice(visibleChipCount!)
                    .map((opt) => opt.label)
                    .join(", ")}
                >
                  <div
                    className={filterClasses(
                      shouldChipsCollapse ? "ml-1" : "ml-0",
                      "relative flex h-full cursor-pointer items-center rounded-[1px] border border-grey-400 bg-white px-1 py-0.5 text-xs"
                    )}
                  >
                    <span>+{hiddenChipCount}</span>
                  </div>
                </Tooltip>
              </div>
            )}
          </>
        )}

        {/* chip variant: icon + label per selected option */}
        {isChipVariant && (
          <div className="flex flex-wrap items-center gap-x-1">
            {hasValue ? (
              selectedOptions.map((option, index) => (
                <Fragment key={option.value}>
                  {index > 0 && <span>,</span>}
                  <span className="flex items-center gap-1">
                    {option.icon && <span className="flex shrink-0 items-center">{option.icon}</span>}
                    <span>{option.label}</span>
                  </span>
                </Fragment>
              ))
            ) : (
              <span className="text-black">{placeholderText}</span>
            )}
          </div>
        )}

        {/* search input */}
        {hasSearchTrigger && (
          <div className={isChipVariant ? "min-w-0 flex-none" : "flex-1"}>
            <input
              type="text"
              placeholder={placeholderText}
              name={name}
              disabled={disabled}
              id={`${id}-input`}
              value={inputValue}
              required={required}
              onChange={onInputChange}
              className={filterClasses(
                "rounded-sm bg-transparent py-1.5 text-sm outline-none",
                isChipVariant
                  ? "field-sizing-content w-auto min-w-[1ch] cursor-pointer placeholder:text-black"
                  : filterClasses("w-full placeholder:text-grey-600", isMultiSelect && selectedOptions.length > 0 ? "pr-2 pl-0" : "pr-2 pl-3"),
                "text-grey-900",

                "caret-orange-500 group-data-[error=true]:caret-red-500",
                "disabled:cursor-not-allowed disabled:bg-transparent disabled:text-grey-500"
              )}
              onFocus={onInputFocus}
              onBlur={onInputBlur}
              ref={inputRef}
              readOnly={isChipVariant}
            />
          </div>
        )}

        {/* single select display or placeholder */}
        {!hasSearchTrigger && !isChipVariant && (
          <>
            <input
              type="text"
              id={id}
              name={name}
              required={required}
              tabIndex={-1}
              readOnly
              aria-hidden="true"
              value={selectedOptions[0]?.value ?? ""}
              className="sr-only"
            />
            <span className={filterClasses("ml-3 flex-1 truncate text-black group-data-[disabled=true]:text-grey-500", !hasValue && "text-grey-600")}>
              {hasValue ? selectedOptions[0]?.label : placeholder || "Select"}
              {/* Detail after the label, dimmed so the label still leads. Inside
                  the same truncating span, so a long pair clips as one string
                  rather than pushing the icons out of the field. */}
              {hasValue && showSublabelInTrigger && selectedOptions[0]?.sublabel && (
                <span className="text-grey-600"> · {selectedOptions[0].sublabel}</span>
              )}
            </span>
          </>
        )}

        {/* icons */}
        <div className="ml-auto flex items-center gap-1">
          {hasValue && !isMultiSelect && !hideClear && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onClearAll();
              }}
              disabled={disabled}
              type="button"
            >
              <IoClose
                className={filterClasses(
                  "size-4 text-grey-600",
                  "group-data-[disabled=true]:cursor-not-allowed group-data-[disabled=true]:text-grey-500 hover:text-orange-500 group-data-[disabled=true]:hover:text-grey-500",
                  "transition-colors duration-150 ease-in-out"
                )}
              />
            </button>
          )}

          {/* right icon */}
          {(hasSearchTrigger || isMultiSelect) && !isChipVariant && isPopoverOpen ? (
            <LuSearch className="size-4 text-grey-600 group-data-[disabled=true]:cursor-not-allowed group-data-[disabled=true]:text-grey-500" />
          ) : (
            <IoIosArrowDown
              className={filterClasses(
                isChipVariant ? "h-3.5 w-3.5" : "size-4",
                "origin-center text-grey-600",
                "group-data-[disabled=true]:text-grey-500",
                "transition-rotate duration-300 ease-in-out",
                isPopoverOpen && "rotate-180"
              )}
            />
          )}
        </div>
      </div>
    );
  }
);

SelectTrigger.displayName = "SelectTrigger";
